/**
 * The one detail view every resource type renders through (GDN-2723).
 *
 * The shell owns the record: it reads it from the Resource Access registry and
 * renders the Fields and Raw JSON tabs from that payload, always. A type may
 * add a `header` (title, status, lifecycle chips), a `preview` tab, and
 * `sections` below the record for related resources it fetches itself — but
 * none of those can replace the record, which is what stops a second view for
 * a type from re-emerging.
 */

import { Component, type ErrorInfo, type ReactNode, Suspense } from 'react';
import { useSearchParams } from 'react-router-dom';
import {
  Box,
  Card,
  CardContent,
  CardHeader,
  Chip,
  CircularProgress,
  Table,
  TableBody,
  TableCell,
  TableRow,
  ToggleButton,
  ToggleButtonGroup,
  Typography,
} from '@mui/material';

import { useGetResourceDetailQuery, useGetResourceTypeSchemaQuery } from '../../store/hooks';
import type { ResourceScope } from '../../utils/resource-routes';
import {
  resolveResourceDetailViewMode,
  type ResourceDetailViewMode,
} from '../../utils/resource-explorer-url';
import { ResourceActivityCard } from '../resource-activity-card';
import type { ResourceSchema } from '../resource-renderer-types';
import { getResourceView } from './registry';
import { humanize, isIsoDate } from './schema-columns';
import type { ResourceRow } from './types';

/** Recursively render an arbitrary JSON value from a resource payload. */
function ResourceValue({ value }: { value: unknown }) {
  if (value === null || value === undefined || value === '') {
    return (
      <Typography component="span" color="text.secondary">
        —
      </Typography>
    );
  }
  if (typeof value === 'boolean') {
    return (
      <Chip size="small" label={value ? 'true' : 'false'} color={value ? 'success' : 'default'} />
    );
  }
  if (typeof value === 'number') return <span>{value}</span>;
  if (typeof value === 'string') {
    return <span>{isIsoDate(value) ? new Date(value).toLocaleString() : value}</span>;
  }
  if (Array.isArray(value)) {
    if (value.length === 0) {
      return (
        <Typography component="span" color="text.secondary">
          (empty)
        </Typography>
      );
    }
    return (
      <Box component="ul" sx={{ m: 0, pl: 2 }}>
        {(value as unknown[]).map((item, i) => (
          <li key={i}>
            <ResourceValue value={item} />
          </li>
        ))}
      </Box>
    );
  }
  if (typeof value === 'object') {
    return <KeyValueTable obj={value as ResourceRow} />;
  }
  return null;
}

function KeyValueTable({ obj, labels }: { obj: ResourceRow; labels?: Record<string, string> }) {
  const keys = Object.keys(obj);
  if (keys.length === 0) {
    return (
      <Typography component="span" color="text.secondary">
        (empty)
      </Typography>
    );
  }
  return (
    <Table
      size="small"
      sx={{
        '& td': {
          borderBottom: '1px solid',
          borderColor: 'divider',
          verticalAlign: 'top',
          overflowWrap: 'anywhere',
        },
      }}
    >
      <TableBody>
        {keys.map(k => (
          <TableRow key={k}>
            <TableCell sx={{ width: 240, fontWeight: 500, color: 'text.secondary' }}>
              {labels?.[k] ?? humanize(k)}
            </TableCell>
            <TableCell>
              <ResourceValue value={obj[k]} />
            </TableCell>
          </TableRow>
        ))}
      </TableBody>
    </Table>
  );
}

/** Labeled key/value driven by the resource's JSON Schema. */
function SchemaFields({ data, schema }: { data: ResourceRow; schema?: ResourceSchema }) {
  const props = schema?.json_schema?.properties ?? {};
  const labels: Record<string, string> = {};
  for (const [k, v] of Object.entries(props)) {
    if (v && typeof v.title === 'string') labels[k] = v.title;
  }
  return <KeyValueTable obj={data} labels={labels} />;
}

class SlotErrorBoundary extends Component<
  { children: ReactNode; what: string },
  { hasError: boolean }
> {
  state = { hasError: false };

  static getDerivedStateFromError() {
    return { hasError: true };
  }

  componentDidCatch(error: Error, info: ErrorInfo) {
    console.error(`Resource ${this.props.what} slot failed`, error, info);
  }

  render() {
    if (this.state.hasError) {
      // A broken per-type slot must not take the record with it — Fields and
      // Raw JSON come from the registry and are still trustworthy.
      return (
        <Typography color="error" sx={{ p: 2 }}>
          This {this.props.what} could not be rendered. The record is still available under Fields
          and Raw JSON.
        </Typography>
      );
    }
    return this.props.children;
  }
}

const Spinner = (
  <Box sx={{ display: 'flex', justifyContent: 'center', p: 3 }}>
    <CircularProgress size={28} />
  </Box>
);

export interface ResourceDetailShellProps {
  resourceType: string;
  resourceId: string;
  scope: ResourceScope;
}

export function ResourceDetailShell({ resourceType, resourceId, scope }: ResourceDetailShellProps) {
  const [searchParams, setSearchParams] = useSearchParams();
  const view = getResourceView(resourceType);

  const {
    data: detail,
    isLoading,
    error,
  } = useGetResourceDetailQuery({
    resourceType,
    resourceId,
    organizationId: scope.organizationId,
  });
  const { data: schema } = useGetResourceTypeSchemaQuery({ resourceType });

  const payload = (detail?.data ?? {}) as ResourceRow;
  // Types whose canonical `get` returns a composite declare how to find the
  // record inside it; everything else is already the record. Doing this once
  // here keeps every slot and the Fields tab agreed on what "the record" is.
  const data = view?.record ? view.record(payload) : payload;
  const Preview = view?.preview;
  const Header = view?.header;
  const Sections = view?.sections;

  const hasPreview = Boolean(Preview);
  const mode = resolveResourceDetailViewMode(searchParams.get('rview'), hasPreview);
  const availableModes: ResourceDetailViewMode[] = hasPreview
    ? ['preview', 'fields', 'raw']
    : ['fields', 'raw'];

  const setMode = (next: ResourceDetailViewMode | null) => {
    if (!next) return;
    const params = new URLSearchParams(searchParams);
    params.set('rview', next);
    setSearchParams(params);
  };

  const slotProps = { data, schema, resourceType, resourceId, scope };

  return (
    <>
      {Header && !isLoading && !error && (
        <SlotErrorBoundary what="header" key={`h:${resourceType}:${resourceId}`}>
          <Header {...slotProps} />
        </SlotErrorBoundary>
      )}

      <Card>
        <CardHeader
          title={view?.label ?? resourceType}
          subheader={resourceId}
          action={
            <ToggleButtonGroup
              exclusive
              size="small"
              value={mode}
              onChange={(_e, next: ResourceDetailViewMode | null) => setMode(next)}
              aria-label="Resource detail view"
            >
              {availableModes.map(m => (
                <ToggleButton key={m} value={m}>
                  {m === 'raw' ? 'Raw JSON' : m[0].toUpperCase() + m.slice(1)}
                </ToggleButton>
              ))}
            </ToggleButtonGroup>
          }
        />
        <CardContent>
          {isLoading ? (
            Spinner
          ) : error ? (
            <Typography color="error">
              Could not load this resource (it may not exist or be out of scope).
            </Typography>
          ) : mode === 'raw' ? (
            // The untouched payload, composite and all — this tab exists to
            // show what the endpoint actually returned.
            <pre
              style={{
                margin: 0,
                fontSize: 12,
                whiteSpace: 'pre-wrap',
                wordBreak: 'break-all',
              }}
            >
              {JSON.stringify(payload, null, 2)}
            </pre>
          ) : mode === 'preview' && Preview ? (
            <SlotErrorBoundary what="preview" key={`p:${resourceType}:${resourceId}`}>
              <Suspense fallback={Spinner}>
                <Preview
                  key={`${resourceType}:${resourceId}`}
                  data={data}
                  schema={schema}
                  resourceType={resourceType}
                  resourceId={resourceId}
                  scope={scope}
                />
              </Suspense>
            </SlotErrorBoundary>
          ) : (
            <SchemaFields data={data} schema={schema} />
          )}
        </CardContent>
      </Card>

      {Sections && !isLoading && !error && (
        <Box sx={{ mt: 3 }}>
          <SlotErrorBoundary what="related records" key={`s:${resourceType}:${resourceId}`}>
            <Suspense fallback={Spinner}>
              <Sections {...slotProps} />
            </Suspense>
          </SlotErrorBoundary>
        </Box>
      )}

      <ResourceActivityCard
        resourceType={resourceType}
        resourceId={resourceId}
        organizationId={scope.organizationId}
      />
    </>
  );
}
